import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import type { Article } from '../../../types/article';

const categoryColors: Record<string, string> = {
  Warzone: 'bg-[#ff6b35]/20 text-[#ff6b35]',
  Multiplayer: 'bg-[#00ff41]/10 text-[#00ff41]',
  Zombies: 'bg-purple-900/40 text-purple-300',
  Esports: 'bg-cyan-900/40 text-cyan-300',
  Leaks: 'bg-yellow-900/40 text-yellow-300',
  Community: 'bg-[#222] text-gray-300',
};

interface ArticleListProps {
  onNew: () => void;
  onEdit: (article: Article) => void;
  hideHeader?: boolean;
}

export default function ArticleList({ onNew, onEdit, hideHeader }: ArticleListProps) {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleCopyUrl = (article: Article) => {
    const slug = article.slug || article.id;
    const url = `${window.location.origin}/news/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(article.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const fetchArticles = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('articles')
      .select('*')
      .order('created_at', { ascending: false });
    setArticles((data as Article[]) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this article permanently? This cannot be undone.')) return;
    setDeletingId(id);
    await supabase.from('articles').delete().eq('id', id);
    setArticles((prev) => prev.filter((a) => a.id !== id));
    setDeletingId(null);
    showToast('Article deleted.');
  };

  const handleTogglePublish = async (article: Article) => {
    const newState = !article.published;
    await supabase.from('articles').update({ published: newState }).eq('id', article.id);
    setArticles((prev) =>
      prev.map((a) => (a.id === article.id ? { ...a, published: newState } : a))
    );
    showToast(newState ? 'Article published!' : 'Article moved to draft.');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const filtered = articles.filter((a) => {
    if (filter === 'published') return a.published;
    if (filter === 'draft') return !a.published;
    return true;
  });

  const publishedCount = articles.filter((a) => a.published).length;
  const draftCount = articles.filter((a) => !a.published).length;

  return (
    <div className={hideHeader ? '' : 'min-h-screen bg-[#0a0a0a]'}>
      {/* Toast */}
      {toast && (
        <div className="fixed top-6 right-6 z-50 bg-[#00ff41] text-black text-sm font-['Barlow_Condensed'] font-bold px-5 py-3 rounded-md shadow-lg">
          {toast}
        </div>
      )}

      {/* Top Bar — hidden when embedded in admin page */}
      {!hideHeader && (
        <header className="border-b border-[#1f1f1f] bg-[#0d0d0d] px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="font-['Barlow_Condensed'] font-black text-xl tracking-tight text-white whitespace-nowrap">
              CALLOFDUTY<span className="text-[#00ff41]">NEXT</span>
            </a>
            <span className="text-[#2a2a2a]">|</span>
            <span className="font-['Barlow_Condensed'] font-bold text-gray-500 tracking-widest text-sm uppercase">Command Center</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/news" target="_blank" rel="nofollow noopener noreferrer" className="text-gray-500 hover:text-white text-sm transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap">
              <i className="ri-external-link-line" /> View Live Site
            </a>
            <button
              onClick={handleLogout}
              className="border border-[#2a2a2a] text-gray-500 hover:text-white hover:border-[#444] text-sm px-4 py-2 rounded-md transition-all cursor-pointer whitespace-nowrap font-['Barlow_Condensed'] font-bold tracking-wider"
            >
              LOGOUT
            </button>
          </div>
        </header>
      )}

      <main className={hideHeader ? '' : 'max-w-6xl mx-auto px-8 py-10'}>
        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 mb-10">
          {[
            { label: 'Total Articles', value: articles.length, icon: 'ri-article-line' },
            { label: 'Published', value: publishedCount, icon: 'ri-checkbox-circle-line', color: 'text-[#00ff41]' },
            { label: 'Drafts', value: draftCount, icon: 'ri-draft-line', color: 'text-[#ff6b35]' },
          ].map((stat) => (
            <div key={stat.label} className="bg-[#111] border border-[#1f1f1f] rounded-lg p-5 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#0a0a0a] rounded-md">
                <i className={`${stat.icon} ${stat.color ?? 'text-gray-400'} text-xl`} />
              </div>
              <div>
                <div className={`font-['Barlow_Condensed'] font-black text-3xl leading-none ${stat.color ?? 'text-white'}`}>
                  {stat.value}
                </div>
                <div className="text-gray-500 text-xs mt-1">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Header Row */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            {(['all', 'published', 'draft'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`font-['Barlow_Condensed'] font-bold text-sm tracking-widest px-4 py-2 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                  filter === f
                    ? 'bg-[#00ff41] border-[#00ff41] text-black'
                    : 'border-[#2a2a2a] text-gray-400 hover:border-[#444] hover:text-white'
                }`}
              >
                {f.toUpperCase()}
              </button>
            ))}
          </div>
          <button
            onClick={onNew}
            className="flex items-center gap-2 bg-[#00ff41] hover:bg-[#00e63a] text-black font-['Barlow_Condensed'] font-black text-sm tracking-widest px-5 py-2 rounded-md transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-base" /> NEW ARTICLE
          </button>
        </div>

        {/* Article Table */}
        {loading ? (
          <div className="text-center py-24 text-gray-600">
            <i className="ri-loader-4-line animate-spin text-3xl mb-3 block" />
            Loading articles...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-[#1f1f1f] rounded-lg">
            <i className="ri-article-line text-4xl text-gray-700 mb-3 block" />
            <p className="text-gray-600 text-sm mb-4">No articles yet. Time to write something!</p>
            <button
              onClick={onNew}
              className="bg-[#00ff41] text-black font-['Barlow_Condensed'] font-black text-sm tracking-wider px-5 py-2 rounded-md cursor-pointer whitespace-nowrap"
            >
              WRITE FIRST ARTICLE
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((article) => (
              <div
                key={article.id}
                className="bg-[#111] border border-[#1f1f1f] rounded-lg p-5 flex items-center gap-4 hover:border-[#2a2a2a] transition-colors"
              >
                {article.image && (
                  <div className="w-16 h-12 rounded overflow-hidden shrink-0">
                    <img src={article.image} alt="" className="w-full h-full object-cover object-top" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-xs font-['Barlow_Condensed'] font-bold tracking-wider px-2 py-0.5 rounded ${categoryColors[article.category] ?? 'bg-gray-800 text-gray-400'}`}>
                      {article.category.toUpperCase()}
                    </span>
                    {article.featured && (
                      <span className="text-xs bg-[#ff6b35]/10 text-[#ff6b35] px-2 py-0.5 rounded font-['Barlow_Condensed'] font-bold">
                        FEATURED
                      </span>
                    )}
                    <span className={`text-xs font-['Barlow_Condensed'] font-bold px-2 py-0.5 rounded ${article.published ? 'bg-[#00ff41]/10 text-[#00ff41]' : 'bg-[#2a2a2a] text-gray-500'}`}>
                      {article.published ? 'LIVE' : 'DRAFT'}
                    </span>
                  </div>
                  <h3 className="text-white text-sm font-medium truncate">{article.title}</h3>
                  <div className="flex items-center gap-3 mt-0.5">
                    <p className="text-gray-600 text-xs">
                      By {article.author} · {article.read_time} min read
                    </p>
                    {article.slug && (
                      <span className="text-[#333] text-xs font-mono truncate max-w-xs">
                        /news/<span className="text-[#555]">{article.slug}</span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleTogglePublish(article)}
                    className={`text-xs font-['Barlow_Condensed'] font-bold px-3 py-1.5 rounded border transition-all cursor-pointer whitespace-nowrap ${
                      article.published
                        ? 'border-[#2a2a2a] text-gray-400 hover:text-[#ff6b35] hover:border-[#ff6b35]/40'
                        : 'border-[#00ff41]/30 text-[#00ff41] hover:bg-[#00ff41]/10'
                    }`}
                  >
                    {article.published ? 'UNPUBLISH' : 'PUBLISH'}
                  </button>
                  <button
                    onClick={() => handleCopyUrl(article)}
                    title="Copy article URL"
                    className={`w-8 h-8 flex items-center justify-center border rounded transition-all cursor-pointer ${
                      copiedId === article.id
                        ? 'border-[#00ff41]/50 text-[#00ff41]'
                        : 'border-[#2a2a2a] text-gray-400 hover:text-white hover:border-[#444]'
                    }`}
                  >
                    <i className={`text-sm ${copiedId === article.id ? 'ri-check-line' : 'ri-link'}`} />
                  </button>
                  <button
                    onClick={() => onEdit(article)}
                    className="w-8 h-8 flex items-center justify-center border border-[#2a2a2a] text-gray-400 hover:text-white hover:border-[#444] rounded transition-all cursor-pointer"
                  >
                    <i className="ri-edit-line text-sm" />
                  </button>
                  <button
                    onClick={() => handleDelete(article.id)}
                    disabled={deletingId === article.id}
                    className="w-8 h-8 flex items-center justify-center border border-[#2a2a2a] text-gray-400 hover:text-red-400 hover:border-red-800/40 rounded transition-all cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
